import React from 'react';
import Card from '../design-system/Card';
import { colors, spacing, radius, fontStack } from '../design-system/tokens';

/**
 * ErrorState — calm, reassuring. Never red, never alarming.
 *
 * Props:
 *   - title   : string    default "Something went wrong"
 *   - message : string?   1-line explanation
 *   - onRetry : ()=>void  optional retry callback
 *   - inline  : boolean   render as a thin strip instead of a Card
 */
export default function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
  inline = false,
}) {
  const content = (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: inline ? 'flex-start' : 'center',
        gap: 8,
        textAlign: inline ? 'left' : 'center',
        fontFamily: fontStack,
        color: colors.textPrimary,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <AlertGlyph />
        <strong style={{ fontSize: 14, fontWeight: 600 }}>{title}</strong>
      </div>
      {message && (
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: colors.textSecondary,
            lineHeight: 1.5,
            maxWidth: 480,
          }}
        >
          {friendly(message)}
        </p>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{
            marginTop: 8,
            padding: '8px 16px',
            borderRadius: radius.sm,
            border: `1px solid ${colors.primary}`,
            background: 'transparent',
            color: colors.primary,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: fontStack,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      )}
    </div>
  );

  if (inline) {
    return (
      <div
        style={{
          padding: '10px 14px',
          border: `1px dashed ${colors.border}`,
          borderRadius: radius.sm,
          background: '#FFF8F7',
        }}
      >
        {content}
      </div>
    );
  }

  return <Card padding={spacing.lg}>{content}</Card>;
}

/* ---- helpers ---- */

// Translate the most common opaque server messages into friendly copy.
// Anything we don't recognise passes through as-is.
function friendly(message) {
  const m = String(message || '').toLowerCase();
  if (m.includes('failed to fetch') || m.includes('networkerror'))
    return 'Network seems unreachable. Check your connection and try again.';
  if (m.includes('timed out') || m.includes('aborted'))
    return 'The request took too long. Please try again.';
  if (m.includes('jwt') || m.includes('row-level security') || m.includes('permission denied'))
    return 'Your session may have expired. Sign in again.';
  if (m.includes('429') || m.includes('rate limit'))
    return 'You are doing that a little too quickly. Wait a moment and retry.';
  if (m.includes('invalid login credentials'))
    return 'That email and password did not match. Try again or reset your password.';
  if (m.includes('user already registered'))
    return 'An account already exists for that email. Try signing in instead.';
  return message;
}

function AlertGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke={colors.textSecondary} strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <circle cx="12" cy="16" r="0.6" fill={colors.textSecondary} />
    </svg>
  );
}
